// Currency Service for live exchange rates
// Uses exchangerate-api.com (free tier: 1500 requests/month)

class CurrencyService {
  static API_BASE = 'https://api.exchangerate-api.com/v4/latest';
  static CACHE_KEY = 'exchange_rates_cache';
  static CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds
  
  // Fallback rates in case API fails
  static FALLBACK_RATES = {
    EUR: 0.92,
    USD: 1.0,
    GBP: 0.79,
    CAD: 1.35
  };

  /**
   * Get live exchange rates with caching
   * @param {string} baseCurrency - Base currency (default: USD)
   * @returns {Promise<Object>} Exchange rates object
   */
  static async getExchangeRates(baseCurrency = 'USD') {
    try {
      // Check cache first
      const cached = this.getCachedRates(baseCurrency);
      if (cached) {
        console.log('Using cached exchange rates');
        return cached;
      }

      // Fetch live rates
      console.log('Fetching live exchange rates...');
      const response = await fetch(`${this.API_BASE}/${baseCurrency}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Cache the results
      this.cacheRates(baseCurrency, data.rates);
      
      console.log('Live exchange rates fetched successfully');
      return data.rates;
      
    } catch (error) {
      console.warn('Failed to fetch live exchange rates, using fallback:', error);
      return this.FALLBACK_RATES;
    }
  }

  /**
   * Convert amount from one currency to another
   * @param {number} amount - Amount to convert
   * @param {string} fromCurrency - Source currency
   * @param {string} toCurrency - Target currency
   * @returns {Promise<number>} Converted amount
   */
  static async convertCurrency(amount, fromCurrency = 'USD', toCurrency = 'EUR') {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    try {
      const rates = await this.getExchangeRates(fromCurrency);
      const rate = rates[toCurrency];
      
      if (!rate) {
        throw new Error(`Rate not found for ${toCurrency}`);
      }
      
      return amount * rate;
    } catch (error) {
      console.error('Currency conversion failed:', error);
      // Use fallback rate
      const fallbackRate = this.FALLBACK_RATES[toCurrency] || 1;
      return amount * fallbackRate;
    }
  }

  /**
   * Get currency info for a specific region
   * @param {string} region - 'us' or 'eu'
   * @returns {Promise<Object>} Currency info with live rates
   */
  static async getCurrencyInfo(region = 'us') {
    const currency = region === 'eu' ? 'EUR' : 'USD';
    const symbol = currency === 'EUR' ? '€' : '$';
    
    if (currency === 'USD') {
      return {
        currency: 'USD',
        rate: 1.0,
        symbol: '$',
        isLive: false
      };
    }

    try {
      const rates = await this.getExchangeRates('USD');
      const rate = rates[currency];
      
      return {
        currency,
        rate,
        symbol,
        isLive: true,
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Failed to get currency info:', error);
      return {
        currency,
        rate: this.FALLBACK_RATES[currency] || 1,
        symbol,
        isLive: false,
        error: true
      };
    }
  }

  /**
   * Cache exchange rates
   * @private
   */
  static cacheRates(baseCurrency, rates) {
    try {
      const cacheData = {
        baseCurrency,
        rates,
        timestamp: Date.now()
      };
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to cache exchange rates:', error);
    }
  }

  /**
   * Get cached exchange rates if still valid
   * @private
   */
  static getCachedRates(baseCurrency) {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // Check if cache is still valid and for the right base currency
      const isValid = (
        cacheData.baseCurrency === baseCurrency &&
        (Date.now() - cacheData.timestamp) < this.CACHE_DURATION
      );
      
      return isValid ? cacheData.rates : null;
    } catch (error) {
      console.warn('Failed to read cached exchange rates:', error);
      return null;
    }
  }

  /**
   * Clear cached rates (useful for testing)
   */
  static clearCache() {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      console.log('Exchange rate cache cleared');
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  /**
   * Format currency with proper symbols and decimals
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code
   * @returns {string} Formatted currency string
   */
  static formatCurrency(amount, currency = 'USD') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Fallback formatting
      const symbol = currency === 'EUR' ? '€' : '$';
      return `${symbol}${amount.toFixed(2)}`;
    }
  }
}

export default CurrencyService; 